// Centralised Schema.org / JSON-LD builders. One canonical Organization (with a
// stable @id) is defined site-wide and referenced by @id from every other
// entity (Service, BlogPosting, Person, WebSite), which is the structure Google
// expects for a coherent entity graph.

import { BASE_URL } from "@/i18n/site";

export const ORG_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;
export const LOGO_URL = "https://www.misupport.dk/wp-content/uploads/2024/09/Logomark-White.png";
export const DEFAULT_OG_IMAGE = "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg";

/** Wrap a JSON-LD object as a head() script entry. */
export const ldScript = (obj: unknown) => ({
  type: "application/ld+json",
  children: JSON.stringify(obj),
});

export function organization() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "MI Support",
    legalName: "MI Support A/S",
    url: BASE_URL,
    logo: { "@type": "ImageObject", "@id": `${BASE_URL}/#logo`, url: LOGO_URL },
    image: { "@id": `${BASE_URL}/#logo` },
    vatID: "DK25793714",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Korskildelund 4, 1.",
      postalCode: "2670",
      addressLocality: "Greve",
      addressCountry: "DK",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+4555551212",
      email: "info@misupport.dk",
      contactType: "customer service",
      areaServed: "DK",
      availableLanguage: ["da", "en", "de"],
    },
    sameAs: [
      "https://www.linkedin.com/company/misupport/",
      "https://www.facebook.com/misupport",
    ],
  };
}

export function website() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: BASE_URL,
    name: "MI Support",
    publisher: { "@id": ORG_ID },
    inLanguage: ["da", "en", "de"],
  };
}

export type Crumb = { name: string; url: string };

export function breadcrumbList(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

export function serviceSchema(opts: {
  name: string;
  description: string;
  serviceType?: string;
  url: string;
  areaServed?: string;
  audienceType?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: opts.serviceType ?? opts.name,
    name: opts.name,
    description: opts.description,
    url: opts.url,
    provider: { "@id": ORG_ID },
    areaServed: { "@type": "Country", name: opts.areaServed ?? "Danmark" },
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.audienceType
      ? { audience: { "@type": "BusinessAudience", audienceType: opts.audienceType } }
      : {}),
  };
}

export function videoObject(opts: {
  name: string;
  description: string;
  contentUrl: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: opts.name,
    description: opts.description,
    contentUrl: opts.contentUrl,
    thumbnailUrl: [opts.thumbnailUrl],
    uploadDate: opts.uploadDate,
    ...(opts.duration ? { duration: opts.duration } : {}),
    publisher: { "@id": ORG_ID },
  };
}

export function personSchema(opts: {
  name: string;
  jobTitle?: string;
  image?: string;
  url: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: opts.name,
    url: opts.url,
    ...(opts.jobTitle ? { jobTitle: opts.jobTitle } : {}),
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.description ? { description: opts.description } : {}),
    worksFor: { "@id": ORG_ID },
  };
}
